/**
 * Map Dashboard — A1-S2 + A1-S3 + A1-S4 + A1-S5
 *
 * Data flow:
 *   1. Zone geometries fetched once from GET /api/v1/geo/zones via authenticated client.
 *   2. DensitySnapshots arrive live via useDensityStream (Core WS → Simulation fallback).
 *   3. Layer toggles (useReducer) show/hide DensityHeatmap and ZoneBoundaryLayer.
 *   4. Playback scrubber provides LIVE/Replay mode UI (A1-S4; historical data in A5-S3).
 *   5. Density calibration slider adjusts heatmap fillOpacity.
 *
 * No PII. All counts are anonymous aggregates.
 */

import { Fragment, useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, ZoomControl, CircleMarker, Marker, Tooltip, Polyline, useMap } from 'react-leaflet';
import { divIcon, latLngBounds, point, type LatLng, type LatLngBounds, type Map as LeafletMap, type LatLngTuple } from 'leaflet';
import type { Alert, DensitySnapshot, Operation, Zone } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { type ColorPalette, Icons, SeverityColor, StatusDot } from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';
import { useDensityData } from '../data/useMockData';
import { HIST_NOW_TICK, MOCK_HISTORY, MOCK_ZONES } from '../data/mock';
import { useFlags } from '../hooks/useFlags';
import { DensityHeatmap } from '../components/DensityHeatmap';
import { alertTypeLabel } from '../utils/alert-type';
import { ZoneBoundaryLayer, compassLabel, ensureCalloutStyles, polygonCentroid } from '../components/ZoneBoundaryLayer';
import { ZoneStatusPanel } from '../components/ZoneStatusPanel';
import { get } from '@vigilo/api-client';
import { crowdQuery } from '../apiScope';
import { useMapProviderConfig, type MapBasemapId } from '../hooks/useMapProviderConfig';
import {
  isZoneMetricFresh,
  useLiveZoneMetrics,
  zoneMetricsDensitySnapshots,
  type CurrentZoneMetrics,
  type DensityHistoryPoint,
  type DensitySnapshotWithFlow,
} from '../hooks/useLiveZoneMetrics';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
} from '../hooks/useCrowdActivityAudit';
import { useZoneAlertSummaries } from '../hooks/useZoneAlertSummaries';
import {
  buildDensitySparkline,
  computeMovementVectorOffset,
  computeSurgeRate5Min,
} from '../utils/zone-metrics-calc';

const MAP_CENTER: [number, number] = [52.3144, 4.9415];
const MAP_ZOOM = 17;
const MINUTE_MS = 60_000;
const DEFAULT_TIMELINE_WINDOW_MINUTES = 60;
const MAX_TIMELINE_WINDOW_MINUTES = 24 * 60;
const DENSITY_CALIBRATION_MIN = 20;
const DENSITY_CALIBRATION_MAX = 100;
const DENSITY_CALIBRATION_STEP = 5;
const DEFAULT_DENSITY_CALIBRATION = 75;
const MAP_MARKER_OFFSET_DEGREES = 0.00008;
const RECENT_HISTORY_LOOKBACK_MINUTES = DEFAULT_TIMELINE_WINDOW_MINUTES + 5;
const RECENT_HISTORY_REFRESH_MS = 45_000;

const BASEMAP_SHORT_LABELS: Record<MapBasemapId, string> = {
  dark: 'Dark',
  satellite: 'Sat',
  light: 'Light',
};

// ── Layer state ───────────────────────────────────────────────────────────────

interface LayerState {
  heatmap:    boolean;
  boundaries: boolean;
  tags:       boolean;
  alerts:     boolean;
  flags:      boolean;
  vectors:    boolean;
}

type LayerAction = { type: 'TOGGLE'; key: keyof LayerState };

function layerReducer(state: LayerState, action: LayerAction): LayerState {
  return { ...state, [action.key]: !state[action.key] };
}

const LAYER_INIT: LayerState = {
  heatmap:    true,
  boundaries: true,
  tags:       true,
  alerts:     true,
  flags:      true,
  vectors:    true,
};

const LAYER_LABELS: Array<[keyof LayerState, string]> = [
  ['heatmap',    'Density heatmap'],
  ['boundaries', 'Zone boundaries'],
  ['tags',       'Zone tags'],
  ['alerts',     'Alert markers'],
  ['flags',      'Flagged tokens'],
  ['vectors',    'Movement vectors'],
];

// ── Playback state ────────────────────────────────────────────────────────────

type PlaybackSpeed = '1x' | '2x' | '4x';
type TimeWindowPreset = '1h';

interface PlaybackState {
  mode:     'live' | 'playback';
  playing:  boolean;
  position: number;
  speed:    PlaybackSpeed;
}

interface TimeWindowState {
  preset: TimeWindowPreset;
  from: string;
  to: string;
}

interface TimelineMarker {
  position: number;
  color: string;
}

type MapProjectionStatus = 'simulation' | 'fresh' | 'stale' | 'missing' | 'loading' | 'error';

interface OperationTimelineBounds {
  startMs: number;
  endMs: number;
  status: Operation['status'];
}

function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function initialTimeWindow(): TimeWindowState {
  const to = new Date();
  const from = new Date(to.getTime() - DEFAULT_TIMELINE_WINDOW_MINUTES * MINUTE_MS);
  return { preset: '1h', from: toDateTimeLocal(from), to: toDateTimeLocal(to) };
}

function timeWindowMinutes(window: TimeWindowState): number {
  const from = new Date(window.from).getTime();
  const to = new Date(window.to).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    return DEFAULT_TIMELINE_WINDOW_MINUTES;
  }
  return Math.max(1, Math.min(MAX_TIMELINE_WINDOW_MINUTES, Math.round((to - from) / MINUTE_MS)));
}

function parseDateMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function operationTimelineBounds(selectedOperation: Operation | null): OperationTimelineBounds | null {
  if (!selectedOperation) return null;

  const operationStartMs = parseDateMs(selectedOperation.actualStart ?? selectedOperation.scheduledStart);
  const scheduledEndMs = parseDateMs(selectedOperation.scheduledEnd);
  const actualEndMs = parseDateMs(selectedOperation.actualEnd);
  const endMs = actualEndMs
    ?? (selectedOperation.status === 'active' || selectedOperation.status === 'paused'
      ? Math.min(Date.now(), scheduledEndMs ?? Date.now())
      : scheduledEndMs);

  if (operationStartMs === null || endMs === null || endMs <= operationStartMs) return null;
  const boundedStartMs = Math.max(operationStartMs, endMs - MAX_TIMELINE_WINDOW_MINUTES * MINUTE_MS);
  return { startMs: boundedStartMs, endMs, status: selectedOperation.status };
}

function clampMs(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampTimeWindowToPolicy(window: TimeWindowState, bounds: OperationTimelineBounds | null): TimeWindowState {
  const fallbackToMs = bounds?.endMs ?? Date.now();
  const fallbackFromMs = fallbackToMs - DEFAULT_TIMELINE_WINDOW_MINUTES * MINUTE_MS;
  let toMs = parseDateMs(window.to) ?? fallbackToMs;
  let fromMs = parseDateMs(window.from) ?? fallbackFromMs;

  if (bounds) {
    toMs = clampMs(toMs, bounds.startMs, bounds.endMs);
    fromMs = clampMs(fromMs, bounds.startMs, bounds.endMs);
  }

  if (toMs <= fromMs) {
    toMs = bounds?.endMs ?? fallbackToMs;
    fromMs = Math.max(bounds?.startMs ?? Number.NEGATIVE_INFINITY, toMs - DEFAULT_TIMELINE_WINDOW_MINUTES * MINUTE_MS);
  }

  if (toMs - fromMs > MAX_TIMELINE_WINDOW_MINUTES * MINUTE_MS) {
    fromMs = toMs - MAX_TIMELINE_WINDOW_MINUTES * MINUTE_MS;
  }

  if (bounds) {
    fromMs = clampMs(fromMs, bounds.startMs, bounds.endMs);
    toMs = clampMs(toMs, bounds.startMs, bounds.endMs);
  }

  return {
    ...window,
    from: toDateTimeLocal(new Date(fromMs)),
    to: toDateTimeLocal(new Date(toMs)),
  };
}

function formatTimelineDateTime(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })} ${fmtHMS(d)}`;
}

// ── Zone fetch ────────────────────────────────────────────────────────────────

async function fetchZones(): Promise<Zone[]> {
  try {
    const body = await get<Zone[] | { data?: Zone[] }>(`/geo/zones?${crowdQuery({ archiveStatus: 'all' })}`);
    return Array.isArray(body) ? body : (body.data ?? []);
  } catch {
    return [];
  }
}

// ── MapController ─────────────────────────────────────────────────────────────

async function fetchDensityHistory(timeWindow: TimeWindowState, operationId: string | null): Promise<DensityHistoryPoint[]> {
  if (!operationId) return [];
  const fromMs = parseDateMs(timeWindow.from);
  const toMs = parseDateMs(timeWindow.to);
  if (fromMs === null || toMs === null || toMs <= fromMs) return [];
  try {
    const params = new URLSearchParams({
      operationId,
      from: new Date(fromMs).toISOString(),
      to: new Date(toMs).toISOString(),
    });
    const body = await get<DensityHistoryPoint[] | { data?: DensityHistoryPoint[] }>(`/live-zone-metrics/density-history?${params.toString()}`);
    return Array.isArray(body) ? body : (body.data ?? []);
  } catch {
    return [];
  }
}

function MapController({
  mapRef,
  onResize,
  onUserInteract,
  onReady,
}: {
  mapRef: React.MutableRefObject<LeafletMap | null>;
  onResize: () => void;
  onUserInteract: () => void;
  onReady: () => void;
}) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; onReady(); }, [map, mapRef, onReady]);
  // The map sits in a flex row beside the zone panel, so its box changes when the panel opens or closes without any
  // window resize. Leaflet only re-reads its size on window resize, so without this the map keeps centring on a stale
  // width and part of the view ends up behind the panel.
  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => map.invalidateSize({ animate: false }), 0);
    });
    observer.observe(container);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [map]);
  useEffect(() => {
    const container = map.getContainer();
    container.addEventListener('wheel', onUserInteract, { passive: true });
    container.addEventListener('pointerdown', onUserInteract);
    map.on('resize', onResize);
    return () => {
      container.removeEventListener('wheel', onUserInteract);
      container.removeEventListener('pointerdown', onUserInteract);
      map.off('resize', onResize);
    };
  }, [map, onResize, onUserInteract]);
  return null;
}

/**
 * The zone to open first: most active alerts, then highest utilisation. Zones without live data rank last, and no pick
 * is made until at least one zone has data. `note` says why, so an operator never wonders why it opened.
 */
function pickPriorityZone(
  zones: Zone[],
  density: Record<string, DensitySnapshot>,
  alertCounts: Record<string, number>,
): { zone: Zone; note: string } | null {
  const rows = zones
    .filter(zone => !zone.archivedAt && zone.geometry.type === 'Polygon')
    .map(zone => ({ zone, alerts: alertCounts[zone.id] ?? 0, util: density[zone.id]?.capacityUtilisation ?? null }));
  if (!rows.some(row => row.util !== null)) return null;
  rows.sort((a, b) => b.alerts - a.alerts || (b.util ?? -1) - (a.util ?? -1));
  const best = rows[0];
  // No numbers here: the panel below shows the live ones, and a quoted figure would soon disagree with them.
  const note = best.alerts > 0 ? 'Most active alerts' : 'Highest utilisation';
  return { zone: best.zone, note };
}

function polygonLatLngs(zone: Zone): LatLngTuple[] {
  if (zone.geometry.type !== 'Polygon') return [];
  const coordinates = zone.geometry.coordinates as number[][][];
  return coordinates.flatMap(ring => ring.map(([lng, lat]) => [lat, lng] as LatLngTuple));
}

function offsetLatLng([lat, lng]: LatLngTuple, latOffset: number, lngOffset: number): LatLngTuple {
  return [lat + latOffset, lng + lngOffset];
}

function alertMarkerPosition(center: LatLngTuple): LatLngTuple {
  return offsetLatLng(center, MAP_MARKER_OFFSET_DEGREES, -MAP_MARKER_OFFSET_DEGREES);
}

function flagMarkerPosition(center: LatLngTuple): LatLngTuple {
  return offsetLatLng(center, -MAP_MARKER_OFFSET_DEGREES, MAP_MARKER_OFFSET_DEGREES);
}

/** "just now", "12 min ago", "1 h 05 min ago". */
function minutesAgoLabel(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (!Number.isFinite(minutes) || Number.isNaN(Date.parse(iso))) return '';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min ago`;
}

const capitalise = (word: string): string => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word);

/** What a pin or arrow is, shown at the pointer in the same dark glass as the zone cards. */
function MapTip({ C, accent, title, detail }: { C: ColorPalette; accent: string; title: string; detail?: string }) {
  return (
    <div style={{
      boxSizing: 'border-box', maxWidth: 240, padding: '7px 11px 8px',
      background: `${C.surface}f7`, border: `1px solid ${C.border2}`, borderLeft: `3px solid ${accent}`,
      borderRadius: 8, boxShadow: '0 8px 22px rgba(0,0,0,0.38)', fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.text }}>{title}</div>
      {detail && <div style={{ marginTop: 3, fontSize: 11, color: C.sub }}>{detail}</div>}
    </div>
  );
}

// Border Security draws vectors long enough to read at map zoom; the raw flow offset is only ~20-40 m here, which
// disappears under zone labels, so stretch it for display.
const MOVEMENT_VECTOR_VISUAL_SCALE = 2.5;

interface ZoneMovementVector {
  positions: [LatLngTuple, LatLngTuple];
  bearing: number;
}

function movementVectorForZone(zone: Zone, flow: DensitySnapshotWithFlow | undefined): ZoneMovementVector | null {
  if (zone.geometry.type !== 'Polygon') return null;
  const offset = computeMovementVectorOffset(flow, zone.capacity);
  if (!offset || flow?.dominantDirection === null || flow?.dominantDirection === undefined) return null;

  const center = polygonCentroid(zone.geometry.coordinates as number[][][]) as LatLngTuple;
  return { positions: [center, offsetLatLng(center, offset[0] * MOVEMENT_VECTOR_VISUAL_SCALE, offset[1] * MOVEMENT_VECTOR_VISUAL_SCALE)], bearing: flow.dominantDirection };
}

// Same arrow treatment as the Border Security map: solid line ending in a small triangle pointing along the bearing.
const arrowIconCache = new Map<string, ReturnType<typeof divIcon>>();
function movementArrowIcon(bearing: number, color: string) {
  const key = `${Math.round(bearing)}|${color}`;
  const cached = arrowIconCache.get(key);
  if (cached) return cached;
  const icon = divIcon({
    className: 'crowd-map-movement-vector',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:8px solid ${color};transform:translate(-50%,-50%) rotate(${bearing}deg);"></div>`,
  });
  arrowIconCache.set(key, icon);
  return icon;
}

type ViewInsets = { top: number; right: number; bottom: number; left: number };

/**
 * How much of each map edge is covered by HTML panels floating over it. Panels opt in with
 * data-map-obstacle="left|right|top|bottom"; Leaflet's own zoom control (top-right) is always counted.
 */
function mapViewInsets(map: LeafletMap): ViewInsets {
  const container = map.getContainer();
  const box = container.getBoundingClientRect();
  const insets: ViewInsets = { top: 0, right: 0, bottom: 0, left: 0 };
  const grow = (side: keyof ViewInsets, value: number) => { insets[side] = Math.max(insets[side], Math.max(0, value)); };

  container.parentElement?.querySelectorAll<HTMLElement>('[data-map-obstacle]').forEach(el => {
    const side = el.dataset.mapObstacle as keyof ViewInsets;
    if (!(side in insets)) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    if (side === 'left') grow(side, r.right - box.left);
    else if (side === 'right') grow(side, box.right - r.left);
    else if (side === 'top') grow(side, r.bottom - box.top);
    else grow(side, box.bottom - r.top);
  });

  const zoom = container.querySelector<HTMLElement>('.leaflet-top.leaflet-right .leaflet-control');
  if (zoom) grow('right', box.right - zoom.getBoundingClientRect().left);
  return insets;
}

const MAP_VIEW_MAX_ZOOM = 19;

const NO_EXTRAS: ViewInsets = { top: 0, right: 0, bottom: 0, left: 0 };
/** The venue never gets less than this share of the free width or height, whatever the cards ask for. */
const MIN_VENUE_SHARE = 0.42;

/**
 * The view that puts `bounds` dead centre in the free area (the map minus every floating panel) with `margin` px of
 * breathing room on each side of it. Landing and zone focus both use this, so they line up at any window size.
 * `extras` is how far zone cards stick out past `bounds` on each side: the subject and its cards are framed as one
 * picture, so the cards get the same breathing room as the zones themselves.
 */
function freeAreaView(
  map: LeafletMap,
  bounds: LatLngBounds,
  margin: (free: { w: number; h: number }) => { x: number; y: number },
  extras: ViewInsets = NO_EXTRAS,
): { center: LatLng; zoom: number } | null {
  const size = map.getSize();
  // On a very small map the panels would swallow most of it: keep at least 30% of each axis free, shrinking the
  // reservations together, so the subject never ends up hidden behind a panel.
  const raw = mapViewInsets(map);
  const reserve = (a: number, b: number, span: number): [number, number] => {
    const total = a + b, limit = span * 0.7;
    const k = total > limit ? limit / total : 1;
    return [a * k, b * k];
  };
  const [insetLeft, insetRight] = reserve(raw.left, raw.right, size.x);
  const [insetTop, insetBottom] = reserve(raw.top, raw.bottom, size.y);
  const insets: ViewInsets = { left: insetLeft, right: insetRight, top: insetTop, bottom: insetBottom };
  const free = { w: size.x - insets.left - insets.right, h: size.y - insets.top - insets.bottom };
  if (free.w < 80 || free.h < 80) return null;

  const wanted = margin(free);
  // Whatever the margins ask for, always leave the subject a usable area.
  const m = { x: Math.min(wanted.x, Math.max(0, (free.w - 160) / 2)), y: Math.min(wanted.y, Math.max(0, (free.h - 160) / 2)) };
  // Cards never take more than a third of an axis per side, however far they stick out.
  const ex: ViewInsets = {
    left: Math.min(extras.left, free.w / 3), right: Math.min(extras.right, free.w / 3),
    top: Math.min(extras.top, free.h / 3), bottom: Math.min(extras.bottom, free.h / 3),
  };
  // However much the cards ask for, the venue itself keeps a readable share of the free area: on a small screen the
  // cards compact (or sit over the venue) instead of shrinking the map picture to a few dots.
  const availW = Math.max(free.w * MIN_VENUE_SHARE, free.w - 2 * m.x - ex.left - ex.right);
  const availH = Math.max(free.h * MIN_VENUE_SHARE, free.h - 2 * m.y - ex.top - ex.bottom);

  // Exact fit (not snapped to the map's zoom step), so the spacing is even whatever the window's shape.
  const z0 = map.getZoom();
  const nw = map.project(bounds.getNorthWest(), z0);
  const se = map.project(bounds.getSouthEast(), z0);
  const scale = Math.min(availW / Math.max(1, se.x - nw.x), availH / Math.max(1, se.y - nw.y));
  const zoom = clamp(z0 + Math.log2(scale), map.getMinZoom(), Math.min(MAP_VIEW_MAX_ZOOM, map.getMaxZoom()));

  // The subject sits between the cards: shift the centre by half the difference in what sticks out each way.
  const freeCentre = point(
    insets.left + free.w / 2 + (ex.left - ex.right) / 2,
    insets.top + free.h / 2 + (ex.top - ex.bottom) / 2,
  );
  const centrePx = map.project(bounds.getCenter(), zoom).add(size.divideBy(2).subtract(freeCentre));
  return { center: map.unproject(centrePx, zoom), zoom };
}

/** How far the cards that are on screen right now stick out past `bounds` on each side, in px. */
function cardExtras(map: LeafletMap, bounds: LatLngBounds, zoneIds: ReadonlySet<string> | null): ViewInsets {
  const container = map.getContainer();
  const box = container.getBoundingClientRect();
  const nw = map.latLngToContainerPoint(bounds.getNorthWest());
  const se = map.latLngToContainerPoint(bounds.getSouthEast());
  const out = { ...NO_EXTRAS };
  container.querySelectorAll<HTMLElement>('.zc-card-group.on [data-zone-card]').forEach(el => {
    if (zoneIds && !zoneIds.has(el.dataset.zoneCard ?? '')) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    out.left = Math.max(out.left, nw.x - (r.left - box.left));
    out.right = Math.max(out.right, (r.right - box.left) - se.x);
    out.top = Math.max(out.top, nw.y - (r.top - box.top));
    out.bottom = Math.max(out.bottom, (r.bottom - box.top) - se.y);
  });
  return out;
}

/** Before any card exists, leave room where the cards will go (the sides first: they are wider than tall). */
function cardAllowance(map: LeafletMap): ViewInsets {
  const size = map.getSize();
  const side = Math.min(190, Math.max(60, size.x * 0.14));
  return { left: side, right: side, top: 52, bottom: 52 };
}

/**
 * Glide to the best view of `bounds` together with the cards now on screen. Does nothing when the map is already
 * there, so calling it again after every layout settles can never chase its own tail.
 */
function composeView(
  map: LeafletMap,
  bounds: LatLngBounds,
  zoneIds: ReadonlySet<string> | null,
  ratio: number,
  tolerance: { px: number; zoom: number },
  animate = true,
  withCards = true,
): boolean {
  if (!bounds.isValid()) return false;
  // `withCards` false fits the venue alone: the few cards that last are placed around or over its edges afterwards, so
  // the picture can be as large as the free area allows.
  const extras = withCards ? cardExtras(map, bounds, zoneIds) : NO_EXTRAS;
  const view = freeAreaView(map, bounds, free => evenMargin(free, ratio), extras);
  if (!view) return false;
  const here = map.project(map.getCenter(), view.zoom);
  const there = map.project(view.center, view.zoom);
  const dz = view.zoom - map.getZoom();
  if (Math.abs(dz) < tolerance.zoom && here.distanceTo(there) < tolerance.px) return false;
  // A background tab gets no animation frames, which would freeze a glide half way: jump there instead.
  if (document.hidden || !animate) map.setView(view.center, view.zoom, { animate: false });
  else if (Math.abs(dz) < 0.02) map.panTo(view.center, { animate: true, duration: 0.9, easeLinearity: 0.2 });
  else map.flyTo(view.center, view.zoom, { duration: 1.1 });
  return true;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** The same pixel margin on all four sides, scaled to the shorter side of the free area. */
function evenMargin(free: { w: number; h: number }, ratio: number): { x: number; y: number } {
  const m = clamp(Math.min(free.w, free.h) * ratio, 20, 72);
  return { x: m, y: m };
}

/** Margin used when the cards are framed along with the subject. */
const COMPOSE_MARGIN_RATIO = 0.07;

/** Flies so the whole zone sits centred in the free area, with the same breathing room on every side. */
function focusZoneInView(map: LeafletMap, zone: Zone, animate = true): void {
  const points = polygonLatLngs(zone);
  if (points.length === 0) return;
  const bounds = latLngBounds(points);
  const view = freeAreaView(map, bounds, free => evenMargin(free, 0.12));
  // A background tab gets no animation frames, which would freeze a fly-to half way: jump there instead.
  if (view && (!animate || document.hidden)) map.setView(view.center, view.zoom, { animate: false });
  else if (view) map.flyTo(view.center, view.zoom, { duration: 0.7 });
  else map.fitBounds(bounds, { padding: [16, 16], maxZoom: MAP_VIEW_MAX_ZOOM, animate });
}

/**
 * Landing view: the whole venue centred in the free area. The margins leave room for the zone callout cards that sit
 * just outside the venue.
 */
function fitZoneLayout(map: LeafletMap, zones: Zone[], leaveRoomForCards = true): boolean {
  const points = zones.flatMap(polygonLatLngs);
  if (points.length === 0) return false;

  const bounds = latLngBounds(points);
  if (!bounds.isValid()) return false;

  // The cards are not placed yet: leave room for them, then composeView() refines the framing once they are.
  const view = freeAreaView(map, bounds, free => evenMargin(free, COMPOSE_MARGIN_RATIO), leaveRoomForCards ? cardAllowance(map) : NO_EXTRAS);
  if (!view) {
    map.fitBounds(bounds, { padding: [16, 16], maxZoom: MAP_VIEW_MAX_ZOOM, animate: false });
    return true;
  }
  map.setView(view.center, view.zoom, { animate: false });
  return true;
}

function MovementVectorLayer({
  zones,
  snapshots,
  C,
  onZoneClick,
}: {
  zones: Zone[];
  snapshots: Record<string, DensitySnapshotWithFlow>;
  C: ColorPalette;
  onZoneClick: (zone: Zone) => void;
}) {
  return (
    <>
      {zones.map(zone => {
        const vector = movementVectorForZone(zone, snapshots[zone.id]);
        if (!vector) return null;

        return (
          <Fragment key={`movement-vector-${zone.id}`}>
            <Polyline
              positions={vector.positions}
              pathOptions={{ color: C.teal, weight: 3, opacity: 0.85, className: 'crowd-map-movement-vector' }}
              eventHandlers={{ click: () => onZoneClick(zone) }}
            >
              <Tooltip sticky direction="top" offset={[0, -8]} className="crowd-hover-tip" opacity={1}>
                <MapTip C={C} accent={C.teal} title={`Crowd movement · ${zone.name}`} detail={compassLabel(vector.bearing) ? `Heading ${compassLabel(vector.bearing)}` : undefined} />
              </Tooltip>
            </Polyline>
            <Marker
              position={vector.positions[1]}
              icon={movementArrowIcon(vector.bearing, C.teal)}
              interactive={false}
              keyboard={false}
            />
          </Fragment>
        );
      })}
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtHMS(d: Date): string {
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map(n => String(n).padStart(2, '0')).join(':');
}

function mockHistoricalDensityForZone(zone: Zone, minutesAgo: number): DensitySnapshot {
  const history = MOCK_HISTORY[zone.id];
  if (!history?.length) {
    return {
      zoneId: zone.id,
      occupancyCount: 0,
      densityScore: 0,
      capacityUtilisation: 0,
      trendDirection: 'stable',
      trendRate: 0,
      timestamp: new Date().toISOString(),
    };
  }

  const tickOffset = Math.round(minutesAgo / 5);
  const index = Math.max(0, Math.min(history.length - 1, HIST_NOW_TICK - tickOffset));
  const point = history[index];
  const prev = history[Math.max(0, index - 1)] ?? point;
  const trendRate = Math.round(((point.capacityUtilisation - prev.capacityUtilisation) / 5) * 10) / 10;
  const trendDirection = trendRate > 0.2 ? 'rising' : trendRate < -0.2 ? 'falling' : 'stable';

  return {
    zoneId: zone.id,
    occupancyCount: point.occupancyCount,
    densityScore: point.capacityUtilisation,
    capacityUtilisation: point.capacityUtilisation,
    trendDirection,
    trendRate,
    timestamp: point.timestamp,
  };
}

function historyPointToDensity(point: DensityHistoryPoint): DensitySnapshot {
  return {
    zoneId: point.zoneId,
    occupancyCount: point.occupancyCount,
    densityScore: point.densityScore,
    capacityUtilisation: point.capacityUtilisation,
    trendDirection: 'stable',
    trendRate: 0,
    timestamp: point.sampledAt,
  };
}

function mapProjectionStatus(
  rows: CurrentZoneMetrics[],
  loading: boolean,
  error: string | null,
  isMock: boolean,
): MapProjectionStatus {
  if (isMock) return 'simulation';
  if (error) return 'error';
  if (loading && rows.length === 0) return 'loading';
  if (rows.some(row => isZoneMetricFresh(row))) return 'fresh';
  return rows.length > 0 ? 'stale' : 'missing';
}

// ── Layer control panel ───────────────────────────────────────────────────────

function LayerPanel({
  C,
  layers,
  dispatch,
  calibration,
  setCalibration,
  basemap,
  setBasemap,
  basemapIds,
  onLayerToggle,
  onBasemapChange,
  onCalibrationCommit,
  hiddenTagCount,
  onRestoreTags,
}: {
  C:              ColorPalette;
  layers:         LayerState;
  dispatch:       React.Dispatch<LayerAction>;
  calibration:    number;
  setCalibration: (v: number) => void;
  basemap:        MapBasemapId;
  setBasemap:     (v: MapBasemapId) => void;
  basemapIds:     MapBasemapId[];
  onLayerToggle:  (key: keyof LayerState, enabled: boolean) => void;
  onBasemapChange: (key: MapBasemapId) => void;
  onCalibrationCommit: (value: number) => void;
  hiddenTagCount: number;
  onRestoreTags: () => void;
}) {
  return (
    <div data-map-obstacle="left" style={{
      position: 'absolute', top: 14, left: 14, zIndex: 500,
      background: `${C.surface}f0`, backdropFilter: 'blur(8px)',
      border: `1px solid ${C.border2}`, borderRadius: 6,
      width: 252, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{
        padding: '8px 10px 8px 12px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Icons.Map size={12} />
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: C.text,
        }}>Map Controls</span>
      </div>

      {/* Basemap selector */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ color: C.sub }}><Icons.Globe size={11} /></span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: C.sub,
          }}>Basemap</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {basemapIds.map(key => {
            const selected = basemap === key;
            return (
              <button
                key={key}
                onClick={() => {
                  setBasemap(key);
                  onBasemapChange(key);
                }}
                style={{
                  flex: 1, fontSize: 10, fontWeight: 700, padding: '5px 4px', borderRadius: 3,
                  background: selected ? C.amberDim : 'transparent',
                  border: `1px solid ${selected ? C.amber + '55' : C.border2}`,
                  color: selected ? C.amber : C.muted,
                  cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}
              >
                {BASEMAP_SHORT_LABELS[key]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Layer toggles */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <span style={{ color: C.sub }}><Icons.Layers size={11} /></span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: C.sub,
          }}>Layers</span>
        </div>
        {LAYER_LABELS.map(([key, label]) => {
          const on = layers[key];
          return (
            <label
              key={key}
              onClick={() => {
                dispatch({ type: 'TOGGLE', key });
                onLayerToggle(key, !on);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'pointer' }}
            >
              <span style={{
                width: 24, height: 14, borderRadius: 7, flexShrink: 0,
                background: on ? C.amber : C.faint,
                position: 'relative', transition: 'all 150ms',
                border: `1px solid ${on ? C.amber : C.border3}`,
                display: 'inline-block',
              }}>
                <span style={{
                  position: 'absolute', top: 1, left: on ? 11 : 1,
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#fff', transition: 'all 150ms',
                }} />
              </span>
              <span style={{ fontSize: 11.5, color: C.sub }}>{label}</span>
            </label>
          );
        })}
        {hiddenTagCount > 0 && (
          <button
            onClick={onRestoreTags}
            style={{
              marginTop: 8, width: '100%', fontSize: 10.5, fontWeight: 700, padding: '5px 8px', borderRadius: 4,
              background: 'transparent', border: `1px dashed ${C.border3}`, color: C.sub, cursor: 'pointer',
              fontFamily: "'Albert Sans', sans-serif", letterSpacing: '0.03em',
            }}
          >
            Restore {hiddenTagCount} hidden {hiddenTagCount === 1 ? 'tag' : 'tags'}
          </button>
        )}
      </div>

      {/* Density calibration */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ color: C.sub }}><Icons.Activity size={11} /></span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: C.sub,
          }}>Density Calibration</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.amber, fontWeight: 700 }}>
            {calibration}%
          </span>
          <span style={{ fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>
            {DENSITY_CALIBRATION_MIN}-{DENSITY_CALIBRATION_MAX} session
          </span>
        </div>
        <input
          type="range" min={DENSITY_CALIBRATION_MIN} max={DENSITY_CALIBRATION_MAX} step={DENSITY_CALIBRATION_STEP}
          value={calibration}
          onChange={e => setCalibration(clampMs(+e.target.value, DENSITY_CALIBRATION_MIN, DENSITY_CALIBRATION_MAX))}
          onMouseUp={e => onCalibrationCommit(clampMs(+(e.currentTarget as HTMLInputElement).value, DENSITY_CALIBRATION_MIN, DENSITY_CALIBRATION_MAX))}
          onTouchEnd={e => onCalibrationCommit(clampMs(+(e.currentTarget as HTMLInputElement).value, DENSITY_CALIBRATION_MIN, DENSITY_CALIBRATION_MAX))}
          onKeyUp={e => onCalibrationCommit(clampMs(+(e.currentTarget as HTMLInputElement).value, DENSITY_CALIBRATION_MIN, DENSITY_CALIBRATION_MAX))}
          style={{ width: '100%', accentColor: C.amber, margin: 0, cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
          <span style={{ fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>{DENSITY_CALIBRATION_MIN}%</span>
          <span style={{ fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>{DENSITY_CALIBRATION_MAX}%</span>
        </div>
      </div>
    </div>
  );
}

// ── Playback scrubber ─────────────────────────────────────────────────────────

function PlaybackScrubber({ C, pb, setPb, timeWindow, windowMinutes, markers, operationBounds, onPlaybackChange }: {
  C:              ColorPalette;
  pb:             PlaybackState;
  setPb:          React.Dispatch<React.SetStateAction<PlaybackState>>;
  timeWindow:     TimeWindowState;
  windowMinutes:  number;
  markers:        TimelineMarker[];
  operationBounds: OperationTimelineBounds | null;
  onPlaybackChange: (metadata: { playbackState?: string; playbackSpeed?: string; control?: string }) => void;
}) {
  const safeWindowMinutes = Math.max(1, windowMinutes);
  const isAtWindowEnd = pb.position >= safeWindowMinutes;
  const isLive = isAtWindowEnd && (operationBounds?.status === 'active' || operationBounds?.status === 'paused');
  const policyLabel = operationBounds
    ? `Operation window ${formatTimelineDateTime(operationBounds.startMs)} - ${formatTimelineDateTime(operationBounds.endMs)} | ${MAX_TIMELINE_WINDOW_MINUTES / 60}h aggregate cap`
    : `${MAX_TIMELINE_WINDOW_MINUTES / 60}h aggregate cap`;

  const positionLabel = () => {
    const fromMs = parseDateMs(timeWindow.from);
    const targetMs = fromMs === null
      ? Date.now()
      : fromMs + Math.min(pb.position, safeWindowMinutes) * MINUTE_MS;
    const minsFromEnd = Math.max(0, safeWindowMinutes - Math.min(pb.position, safeWindowMinutes));
    if (minsFromEnd === 0) return `${fmtHMS(new Date(targetMs))} - ${isLive ? 'now' : 'window end'}`;
    return `${fmtHMS(new Date(targetMs))} - t-${minsFromEnd}m`;
  };

  return (
    <div data-map-obstacle="bottom" style={{
      position: 'absolute', bottom: 52, left: 14, right: 14, zIndex: 500,
      background: `${C.surface}f0`, backdropFilter: 'blur(8px)',
      border: `1px solid ${C.border2}`, borderRadius: 6,
      padding: '6px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap',
      fontFamily: "'Albert Sans', sans-serif",
      minHeight: 34,
    }}>

      {/* Play / Pause */}
      <button
        onClick={() => {
          setPb(p => {
            const playing = !p.playing;
            onPlaybackChange({ playbackState: playing ? 'playing' : 'paused', control: 'play_pause' });
            return { ...p, playing };
          });
        }}
        style={{
          width: 26, height: 26, borderRadius: '50%', border: 'none',
          background: C.amber, color: '#fff', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {pb.playing
          ? <Icons.Pause size={11} />
          : <Icons.Play  size={11} />}
      </button>

      {/* Status + time label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, minWidth: 0 }}>
        <StatusDot color={isLive ? C.green : C.amber} pulse={isLive && pb.playing} size={5} />
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: isLive ? C.green : C.amber,
          fontFamily: "'Albert Sans', sans-serif",
        }}>
          {isLive ? 'Live' : 'Replay'}
        </span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.sub, marginLeft: 4 }}>
          {positionLabel()}
        </span>
      </div>

      <div style={{
        flex: '0 1 300px',
        minWidth: 120,
        fontSize: 10.5, lineHeight: '13.3px',
        color: C.muted,
        fontFamily: "'Albert Sans', sans-serif",
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {policyLabel}
      </div>

      {/* Scrubber track */}
      <div style={{ flex: 1, minWidth: 220, position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, background: C.faint, borderRadius: 2 }} />
        <div style={{
          position: 'absolute', left: 0, height: 4,
          width: `${(Math.min(pb.position, safeWindowMinutes) / safeWindowMinutes) * 100}%`,
          background: C.amber,
          borderRadius: 2, transition: 'width 300ms ease',
        }} />
        {/* Alert event markers */}
        {markers.map((m, i) => (
          <div key={i} style={{
            position: 'absolute', left: `${(Math.min(m.position, safeWindowMinutes) / safeWindowMinutes) * 100}%`, transform: 'translateX(-50%)',
            width: 3, height: 12, background: m.color, borderRadius: 1,
          }} />
        ))}
        <input
          type="range" min={0} max={safeWindowMinutes} value={Math.min(pb.position, safeWindowMinutes)}
          onChange={e => {
            setPb(p => ({ ...p, position: +e.target.value, mode: 'playback', playing: false }));
          }}
          onMouseUp={() => onPlaybackChange({ playbackState: 'scrubbed', control: 'scrubber' })}
          onTouchEnd={() => onPlaybackChange({ playbackState: 'scrubbed', control: 'scrubber' })}
          onKeyUp={() => onPlaybackChange({ playbackState: 'scrubbed', control: 'scrubber' })}
          style={{
            position: 'absolute', inset: 0, width: '100%',
            opacity: 0, cursor: 'pointer', margin: 0, padding: 0,
          }}
        />
        {/* Thumb */}
        <div style={{
          position: 'absolute', left: `calc(${(Math.min(pb.position, safeWindowMinutes) / safeWindowMinutes) * 100}% - 7px)`,
          width: 14, height: 14, borderRadius: '50%', background: '#fff',
          border: `2px solid ${C.amber}`,
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)', pointerEvents: 'none',
        }} />
      </div>

      {/* Speed buttons */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {(['1x', '2x', '4x'] as PlaybackSpeed[]).map(s => (
          <button
            key={s}
            onClick={() => {
              setPb(p => ({ ...p, speed: s }));
              onPlaybackChange({ playbackSpeed: s, control: 'speed' });
            }}
            style={{
              fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 3,
              background: pb.speed === s ? C.amberDim : 'transparent',
              border: `1px solid ${pb.speed === s ? C.amber + '55' : C.border2}`,
              color: pb.speed === s ? C.amber : C.muted,
              cursor: 'pointer', fontFamily: 'monospace',
            }}
          >{s}</button>
        ))}
      </div>
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

const alertLabel = alertTypeLabel;

function alertTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--:--';
  return fmtHMS(d);
}

function mapAlertZoneDisplayName(alert: Alert, operationZoneNameById: ReadonlyMap<string, string>): string {
  const operationZoneName = operationZoneNameById.get(alert.zoneId)?.trim();
  if (operationZoneName) return operationZoneName;

  const projectedZoneName = alert.zoneName.trim();
  return projectedZoneName || alert.zoneId;
}

function AlertTicker({ C, alerts: allAlerts, zoneNameById, onAlertClick }: {
  C: ColorPalette;
  alerts: Alert[];
  zoneNameById: ReadonlyMap<string, string>;
  onAlertClick: (a: Alert) => void;
}) {
  const alerts = allAlerts
    .filter(a => a.status !== 'resolved')
    .slice()
    .sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime())
    .slice(0, 8);
  const items = alerts.length > 0 ? [...alerts, ...alerts] : [];

  return (
    <div data-map-obstacle="bottom" style={{
      position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 500,
      height: 44, background: `${C.surface}f0`, backdropFilter: 'blur(8px)',
      borderTop: `1px solid ${C.border2}`,
      display: 'flex', alignItems: 'stretch', overflow: 'hidden',
      fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 14px',
        borderRight: `1px solid ${C.border}`, gap: 6, flexShrink: 0,
        background: C.greenDim,
      }}>
        <span style={{
          width: 6, height: 6, background: C.green, borderRadius: '50%',
          boxShadow: `0 0 0 3px ${C.green}33`, animation: 'alert-pulse 1.6s infinite',
        }} />
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: C.green,
        }}>Live Alerts</span>
      </div>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {items.length > 0 ? (
          <div className="alert-ticker-track" style={{
            display: 'flex', alignItems: 'center', gap: 0,
            position: 'absolute', left: 0, top: 0, bottom: 0,
            animation: 'ticker-scroll 60s linear infinite',
            whiteSpace: 'nowrap',
          }}>
            {items.map((a, i) => {
              const zoneDisplayName = mapAlertZoneDisplayName(a, zoneNameById);
              return (
                <button key={`${a.id}-${i}`} onClick={() => onAlertClick(a)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '0 18px', height: '100%',
                  background: 'transparent', border: 'none', borderRight: `1px solid ${C.border}`,
                  cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
                }}>
                  <StatusDot color={SeverityColor(C, a.severity)} pulse={a.severity === 'critical'} size={6} />
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: SeverityColor(C, a.severity),
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>{alertLabel(a.alertType)}</span>
                  <span style={{ fontSize: 10.5, color: C.text, fontWeight: 600 }}>{zoneDisplayName}</span>
                  <span style={{ fontSize: 10.5, lineHeight: '14px', color: C.muted, fontFamily: 'monospace' }}>{alertTime(a.triggeredAt)}</span>
                  <span style={{ fontSize: 10.5, lineHeight: '14px', color: C.muted }}>- {a.triggerContext.condition}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: 11, color: C.muted }}>
            No open crowd alerts
          </div>
        )}
      </div>
    </div>
  );
}

export function MapDashboard() {
  const { theme, selectedOperation, selectedOperationAlerts } = useApp();
  const C = getCrowdC(theme);
  const recordActivity = useCrowdActivityAudit();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const focusZoneId = searchParams.get('zoneId') ?? (location.state as { focusZoneId?: string } | null)?.focusZoneId ?? null;

  const [allZones,       setAllZones]      = useState<Zone[]>([]);
  const [densityHistory, setDensityHistory] = useState<DensityHistoryPoint[]>([]);
  // Rolling (to = now) history used for the zone panel sparkline and surge rate; the
  // playback densityHistory window is fixed at mount and would otherwise freeze these.
  const [recentHistory, setRecentHistory] = useState<DensityHistoryPoint[]>([]);
  const [calibration,   setCalibration]   = useState(DEFAULT_DENSITY_CALIBRATION);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [hiddenTagIds, setHiddenTagIds] = useState<ReadonlySet<string>>(() => new Set());
  const hideTag = useCallback((zoneId: string) => setHiddenTagIds(prev => new Set(prev).add(zoneId)), []);
  const [focusRequest, setFocusRequest] = useState<{ zoneId: string; nonce: number } | null>(null);
  // The zone the operator most needs first, chosen once per operation when they land (see pickPriorityZone).
  const [landingPick, setLandingPick] = useState<{ zoneId: string; note: string } | null>(null);
  const [landingReady, setLandingReady] = useState(false);
  const [alertsGrace, setAlertsGrace] = useState(false);
  // The map stays invisible until it has been framed, then fades in once: no intermediate views.
  const [landingShown, setLandingShown] = useState(false);
  const landingShownRef = useRef(false);
  landingShownRef.current = landingShown;
  const silentPasses = useRef(0);
  const pickedForOperation = useRef<string | null>(null);
  const composeSignature = useRef('');
  // Set once the first cards have faded (the camera then frames the venue alone), and short "hands off" windows during
  // which the map resizing under a click or a panel close must not cut the view before the intended glide.
  const revealEnded = useRef(false);
  const handsOffUntil = useRef(0);
  const composePasses = useRef(0);
  const [basemap, setBasemap] = useState<MapBasemapId>('dark');
  const [usingBasemapFallback, setUsingBasemapFallback] = useState(false);

  const [layers,   dispatchLayer] = useReducer(layerReducer, LAYER_INIT);
  const [timeWindow, setTimeWindow] = useState<TimeWindowState>(() => initialTimeWindow());
  const windowMinutes = timeWindowMinutes(timeWindow);
  const [playback, setPlayback]   = useState<PlaybackState>({
    mode: 'live', playing: true, position: DEFAULT_TIMELINE_WINDOW_MINUTES, speed: '1x',
  });
  const operationBounds = useMemo(
    () => operationTimelineBounds(selectedOperation),
    [selectedOperation],
  );
  const playbackBottom = 52;
  const playbackHeight = 48;
  const providerMessageBottom = playbackBottom + playbackHeight + 18;

  const mapRef = useRef<LeafletMap | null>(null);
  const zonesRef = useRef<Zone[]>([]);
  const framing = useRef<'landing' | 'user' | { zoneId: string }>('landing');
  // The map is created after the first render, so effects that need it must re-run once it exists.
  const [mapReady, setMapReady] = useState(false);
  const markMapReady = useCallback(() => setMapReady(true), []);
  const didFitZoneLayout = useRef(false);
  const tileErrorCount = useRef(0);
  const { isMock } = useMockMode();
  const { snapshots } = useDensityData();
  const { flags } = useFlags();
  const liveZoneMetrics = useLiveZoneMetrics(selectedOperation?.id ?? null, Boolean(!isMock && selectedOperation?.status === 'active'), 2000);
  const {
    basemaps,
    basemapIds,
    defaultBasemap,
    loading: basemapsLoading,
    error: basemapsError,
  } = useMapProviderConfig();

  useEffect(() => {
    if (!basemaps[basemap] && basemaps[defaultBasemap]) {
      setBasemap(defaultBasemap);
    }
  }, [basemap, basemaps, defaultBasemap]);

  useEffect(() => {
    setTimeWindow(current => clampTimeWindowToPolicy(current, operationBounds));
  }, [operationBounds?.startMs, operationBounds?.endMs]);

  useEffect(() => {
    setPlayback(current => ({
      ...current,
      position: Math.min(current.position, Math.max(1, windowMinutes)),
    }));
  }, [windowMinutes]);

  useEffect(() => {
    if (isMock) {
      setAllZones(MOCK_ZONES);
      setSelectedZoneId(prev => focusZoneId ?? prev);
      return;
    }
    (async () => {
      const z = await fetchZones();
      setAllZones(z);
      if (z.length > 0) setSelectedZoneId(prev => focusZoneId ?? prev);
    })();
  }, [focusZoneId, isMock]);

  useEffect(() => {
    if (isMock) {
      setDensityHistory([]);
      return;
    }

    let cancelled = false;
    void fetchDensityHistory(timeWindow, selectedOperation?.id ?? null).then(points => {
      if (!cancelled) setDensityHistory(points);
    });
    return () => { cancelled = true; };
  }, [isMock, selectedOperation?.id, timeWindow.from, timeWindow.to]);

  useEffect(() => {
    const operationId = selectedOperation?.id ?? null;
    if (isMock || !operationId) {
      setRecentHistory([]);
      return undefined;
    }

    let cancelled = false;
    const load = () => {
      const nowMs = Date.now();
      const rolling: TimeWindowState = {
        preset: '1h',
        from: new Date(nowMs - RECENT_HISTORY_LOOKBACK_MINUTES * MINUTE_MS).toISOString(),
        to: new Date(nowMs).toISOString(),
      };
      void fetchDensityHistory(rolling, operationId).then(points => {
        if (!cancelled) setRecentHistory(points);
      });
    };
    load();
    const timer = window.setInterval(load, RECENT_HISTORY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isMock, selectedOperation?.id]);

  const scopedZones = useMemo(() => {
    if (!selectedOperation) return allZones;
    const assignedZoneIds = new Set(selectedOperation.assignedZoneIds);
    return allZones.filter(zone => assignedZoneIds.has(zone.id));
  }, [allZones, selectedOperation]);

  const zones = useMemo(() => {
    const active = scopedZones.filter(zone => !zone.archivedAt);
    const selectedArchived = selectedZoneId
      ? scopedZones.find(zone => zone.id === selectedZoneId && !!zone.archivedAt)
      : null;
    return selectedArchived ? [...active, selectedArchived] : active;
  }, [scopedZones, selectedZoneId]);

  const zoneIdSet = useMemo(() => new Set(zones.map(zone => zone.id)), [zones]);
  const zoneAlertSummaries = useZoneAlertSummaries(
    selectedOperation?.id ?? null,
    zones.map(zone => zone.id),
    Boolean(!isMock && selectedOperation),
  );
  const operationZoneNameById = useMemo(
    () => new Map(
      zones
        .map(zone => [zone.id, zone.name.trim()] as const)
        .filter(([, name]) => name.length > 0),
    ),
    [zones],
  );
  const scopedMetricRows = useMemo(
    () => liveZoneMetrics.rows.filter(row => zoneIdSet.has(row.zoneId)),
    [liveZoneMetrics.rows, zoneIdSet],
  );
  const freshZoneMetricRows = useMemo(
    () => scopedMetricRows.filter(row => isZoneMetricFresh(row)),
    [scopedMetricRows],
  );
  const freshMetricsByZone = useMemo(
    () => Object.fromEntries(freshZoneMetricRows.map(row => [row.zoneId, row])),
    [freshZoneMetricRows],
  );
  const projectedSnapshots = useMemo(
    () => zoneMetricsDensitySnapshots(freshZoneMetricRows),
    [freshZoneMetricRows],
  );
  const projectionStatus = mapProjectionStatus(
    scopedMetricRows,
    liveZoneMetrics.loading,
    liveZoneMetrics.error,
    isMock,
  );
  // density_history stores no trend/flow fields, so historical playback points carry a
  // hardcoded 'stable' trend and no flow bearing: label it unavailable, and hide vectors.
  const isPlaybackTrendUnavailable = playback.position < windowMinutes && !isMock && densityHistory.length > 0;
  const displayDensity = useMemo(() => {
    if (playback.position >= windowMinutes) return isMock ? snapshots : projectedSnapshots;

    const fromMs = parseDateMs(timeWindow.from);
    const targetMs = fromMs === null ? Number.NaN : fromMs + playback.position * MINUTE_MS;
    if (densityHistory.length > 0 && Number.isFinite(targetMs)) {
      const closestByZone = new Map<string, { distance: number; point: DensityHistoryPoint }>();
      for (const point of densityHistory) {
        const sampledMs = new Date(point.sampledAt).getTime();
        if (!Number.isFinite(sampledMs)) continue;
        const distance = Math.abs(sampledMs - targetMs);
        const current = closestByZone.get(point.zoneId);
        if (!current || distance < current.distance) {
          closestByZone.set(point.zoneId, { distance, point });
        }
      }
      return Object.fromEntries(
        zones
          .map(zone => {
            const match = closestByZone.get(zone.id)?.point;
            return match ? [zone.id, historyPointToDensity(match)] : null;
          })
          .filter((entry): entry is [string, DensitySnapshot] => Boolean(entry)),
      );
    }

    if (!isMock) return {};

    const minutesAgo = windowMinutes - playback.position;
    return Object.fromEntries(zones.map(z => [z.id, mockHistoricalDensityForZone(z, minutesAgo)]));
  }, [densityHistory, isMock, playback.position, projectedSnapshots, snapshots, timeWindow.from, windowMinutes, zones]);

  const scopedFlags = useMemo(() => {
    if (!selectedOperation) return flags;
    return flags.filter(flag =>
      flag.monitoredZoneIds.some(zoneId => selectedOperation.assignedZoneIds.includes(zoneId)),
    );
  }, [flags, selectedOperation]);

  const activeAlerts = useMemo(() => (
    selectedOperationAlerts.filter(alert =>
      alert.status !== 'resolved' &&
      (!selectedOperation || selectedOperation.assignedZoneIds.includes(alert.zoneId)),
    )
  ), [selectedOperation, selectedOperationAlerts]);

  const activeAlertCountByZone = useMemo(() => {
    // Same source as the zone panel (the alert summary), falling back to the live stream, so both always agree.
    const counts: Record<string, number> = {};
    for (const alert of activeAlerts) counts[alert.zoneId] = (counts[alert.zoneId] ?? 0) + 1;
    zoneAlertSummaries.summariesByZoneId.forEach((summary, zoneId) => { counts[zoneId] = summary.active; });
    return counts;
  }, [activeAlerts, zoneAlertSummaries.summariesByZoneId]);

  const timelineMarkers = useMemo(() => {
    const fromMs = new Date(timeWindow.from).getTime();
    const toMs = new Date(timeWindow.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return [];

    return activeAlerts
      .map(alert => {
        const triggeredMs = new Date(alert.triggeredAt).getTime();
        if (!Number.isFinite(triggeredMs) || triggeredMs < fromMs || triggeredMs > toMs) return null;
        return {
          position: Math.round((triggeredMs - fromMs) / MINUTE_MS),
          color: SeverityColor(C, alert.severity),
        };
      })
      .filter((marker): marker is TimelineMarker => Boolean(marker));
  }, [C, activeAlerts, timeWindow.from, timeWindow.to]);

  // Advance playback position in replay mode
  useEffect(() => {
    if (playback.mode !== 'playback' || !playback.playing) return;
    const mult = playback.speed === '4x' ? 4 : playback.speed === '2x' ? 2 : 1;
    const id = setInterval(() => {
      setPlayback(p => {
        if (p.position >= windowMinutes) return { ...p, playing: false };
        return { ...p, position: Math.min(windowMinutes, p.position + mult) };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [playback.mode, playback.playing, playback.speed, windowMinutes]);

  zonesRef.current = zones;

  // Opening or closing the zone panel resizes the map box. The ResizeObserver covers it, but a timer-driven check is
  // cheap and also works where observer callbacks are deferred (background or occluded windows).
  const panelOpen = selectedZoneId !== null;
  useEffect(() => {
    const id = setTimeout(() => mapRef.current?.invalidateSize({ animate: false }), 0);
    return () => clearTimeout(id);
  }, [panelOpen, mapReady]);

  const selectZone = useCallback((zone: Zone) => {
    handsOffUntil.current = Date.now() + 900;
    setSelectedZoneId(zone.id);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
      page: '/map',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
      resourceId: zone.id,
      metadata: { sourcePage: '/map' },
    });
    // The flight happens in an effect, after React has applied the new layout (the zone panel may be opening).
    setFocusRequest(prev => ({ zoneId: zone.id, nonce: (prev?.nonce ?? 0) + 1 }));
  }, [recordActivity, selectedOperation?.id]);

  useEffect(() => {
    if (!focusRequest || !mapRef.current) return;
    const zone = zonesRef.current.find(row => row.id === focusRequest.zoneId);
    if (!zone) return;
    mapRef.current.invalidateSize({ animate: false });
    framing.current = { zoneId: zone.id };
    focusZoneInView(mapRef.current, zone);
  }, [focusRequest]);

  const recordMapControl = useCallback((control: string, value: string | number | boolean) => {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.mapControlChanged,
      page: '/map',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.mapControl,
      resourceId: control,
      metadata: { control, enabled: typeof value === 'boolean' ? value : undefined, layer: typeof value === 'string' ? value : undefined },
    });
  }, [recordActivity, selectedOperation?.id]);

  const recordPlayback = useCallback((metadata: { playbackState?: string; playbackSpeed?: string; control?: string }) => {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.playbackChanged,
      page: '/map',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.playback,
      resourceId: 'map-playback',
      metadata,
    });
  }, [recordActivity, selectedOperation?.id]);

  const recordAlertDrilldown = useCallback((alert: Alert) => {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/map',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.alert,
      resourceId: alert.id,
      metadata: { sourcePage: '/map', targetPage: '/map' },
    });
  }, [recordActivity, selectedOperation?.id]);

  const selectedZone = zones.find(z => z.id === selectedZoneId) ?? null;
  const selectedZoneMetric = selectedZone ? freshMetricsByZone[selectedZone.id] : undefined;
  const selectedZoneAlertCount = selectedZone
    ? zoneAlertSummaries.summariesByZoneId.get(selectedZone.id)?.active
      ?? activeAlerts.filter(alert => alert.zoneId === selectedZone.id).length
    : 0;
  const selectedZoneFlagCount = selectedZone
    ? scopedFlags.filter(flag => flag.monitoredZoneIds.includes(selectedZone.id)).length
    : 0;

  const selectedZoneDensitySparkline = useMemo<number[]>(() => {
    if (!selectedZone) return [];
    if (isMock) {
      const points: number[] = [];
      for (let minutesAgo = DEFAULT_TIMELINE_WINDOW_MINUTES; minutesAgo >= 0; minutesAgo -= 5) {
        points.push(mockHistoricalDensityForZone(selectedZone, minutesAgo).capacityUtilisation);
      }
      return points;
    }
    const cutoffMs = Date.now() - DEFAULT_TIMELINE_WINDOW_MINUTES * MINUTE_MS;
    return buildDensitySparkline(
      recentHistory.filter(point => new Date(point.sampledAt).getTime() >= cutoffMs),
      selectedZone.id,
    );
  }, [selectedZone, isMock, recentHistory]);

  const selectedZoneSurgeRate = useMemo<number | null>(() => {
    if (!selectedZone) return null;
    const snap = projectedSnapshots[selectedZone.id] ?? displayDensity[selectedZone.id];
    if (!snap) return null;
    if (isMock) {
      return snap.capacityUtilisation - mockHistoricalDensityForZone(selectedZone, 5).capacityUtilisation;
    }
    return computeSurgeRate5Min(
      snap.capacityUtilisation,
      recentHistory.filter(point => point.zoneId === selectedZone.id),
      Date.now(),
    );
  }, [selectedZone, isMock, recentHistory, projectedSnapshots, displayDensity]);

  useEffect(() => { setHiddenTagIds(new Set()); }, [selectedOperation?.id]);

  useEffect(() => {
    didFitZoneLayout.current = false;
    pickedForOperation.current = null;
    setLandingPick(null);
    setLandingReady(false);
    setAlertsGrace(false);
    setLandingShown(false);
    silentPasses.current = 0;
    revealEnded.current = false;
    // Never wait on data for long: whatever has arrived by then decides the landing, and the map frames regardless.
    const grace = setTimeout(() => setAlertsGrace(true), 600);
    const give = setTimeout(() => setLandingReady(true), 2200);
    const show = setTimeout(() => setLandingShown(true), 4000);
    return () => { clearTimeout(grace); clearTimeout(give); clearTimeout(show); };
  }, [selectedOperation?.id]);

  // Land on the zone that matters most (once per operation). The panel opens first, so the map is framed only once.
  useEffect(() => {
    if (focusZoneId) { setLandingReady(true); setLandingShown(true); return; }
    const operationKey = selectedOperation?.id ?? '';
    if (pickedForOperation.current === operationKey) return;
    if (!isMock && !alertsGrace && zoneAlertSummaries.summariesByZoneId.size === 0) return;
    const pick = pickPriorityZone(zones, displayDensity, activeAlertCountByZone);
    if (!pick) return;
    pickedForOperation.current = operationKey;
    setSelectedZoneId(pick.zone.id);
    setLandingPick({ zoneId: pick.zone.id, note: pick.note });
    setLandingReady(true);
  }, [focusZoneId, selectedOperation?.id, isMock, alertsGrace, zoneAlertSummaries.summariesByZoneId, zones, displayDensity, activeAlertCountByZone]);

  useEffect(() => {
    if (zones.length === 0) {
      setSelectedZoneId(prev => prev === null ? prev : null);
      return;
    }
    if (focusZoneId && zones.some(zone => zone.id === focusZoneId)) {
      setSelectedZoneId(prev => prev === focusZoneId ? prev : focusZoneId);
      return;
    }
    // No zone is pre-selected: the operator lands on the whole venue and opens a zone by choosing it.
    if (selectedZoneId && !zones.some(zone => zone.id === selectedZoneId)) setSelectedZoneId(null);
  }, [focusZoneId, selectedZoneId, zones]);

  useEffect(() => {
    if (focusZoneId || !landingReady || didFitZoneLayout.current || !mapRef.current) return;
    // The zone panel may already be open, which changes the map's box: make sure Leaflet has measured it first.
    mapRef.current.invalidateSize({ animate: false });
    const activeZones = scopedZones.filter(zone => !zone.archivedAt);
    if (fitZoneLayout(mapRef.current, activeZones)) didFitZoneLayout.current = true;
  }, [focusZoneId, landingReady, scopedZones, mapReady]);

  // Keep the landing view filling the map at any window size or aspect ratio, until the user pans or zooms.
  // Who owns the camera: the landing fit, a focused zone, or the operator (after panning/zooming by hand).
  // A resize re-frames according to the owner, so a zone click is not undone when the panel opens or the window changes.
  const scopedZonesRef = useRef(scopedZones);
  scopedZonesRef.current = scopedZones;
  const markUserMovedMap = useCallback(() => { framing.current = 'user'; setLandingShown(true); }, []);
  useEffect(ensureCalloutStyles, []);
  // MapContainer freezes its style prop at first render, so the fade-in is applied to the map element directly.
  useEffect(() => {
    const el = mapRef.current?.getContainer();
    if (el) el.style.opacity = landingShown ? '1' : '0';
  }, [landingShown, mapReady]);
  useEffect(() => {
    if (landingReady && scopedZones.every(zone => zone.archivedAt)) setLandingShown(true);
  }, [landingReady, scopedZones]);
  const refitOnResize = useCallback(() => {
    const map = mapRef.current;
    const owner = framing.current;
    if (!map || owner === 'user') return;
    // A click or a panel close is about to glide the map to where it belongs; cutting there first would make two moves.
    if (Date.now() < handsOffUntil.current) return;
    if (owner === 'landing') {
      if (!focusZoneId) fitZoneLayout(map, scopedZonesRef.current.filter(zone => !zone.archivedAt), !revealEnded.current);
      return;
    }
    const zone = zonesRef.current.find(row => row.id === owner.zoneId);
    if (zone) focusZoneInView(map, zone, false);
  }, [focusZoneId]);
  useEffect(() => { framing.current = 'landing'; }, [selectedOperation?.id]);

  // Once the cards are placed (or have faded), frame the subject together with the cards still on screen, so the
  // picture is evenly spaced on all four sides. It glides, never jumps, and it stands down once the operator moves the map.
  const recompose = useCallback((revealed = false) => {
    if (landingShownRef.current) revealEnded.current = !revealed;
    const map = mapRef.current;
    const owner = framing.current;
    if (!map || owner === 'user') return;
    if (owner === 'landing' && !focusZoneId) {
      const landingPoints = scopedZonesRef.current.filter(zone => !zone.archivedAt).flatMap(polygonLatLngs);
      if (landingPoints.length === 0) return;
      if (!landingShownRef.current) {
        // Still invisible: settle the framing by jumping (no glide to see), then fade the map in once.
        if (silentPasses.current < 1 && composeView(map, latLngBounds(landingPoints), null, COMPOSE_MARGIN_RATIO, { px: 6, zoom: 0.03 }, false)) {
          silentPasses.current += 1;
          return;
        }
        setLandingShown(true);
        return;
      }
      // While the first cards are up the picture stays still; the camera moves once they fade.
      if (revealed) return;
    }
    // One move, never two. A clicked zone is framed by its own fly-to and then left alone; the landing view makes a
    // single glide once the first cards have faded (or once a card that lasts appears), and only if it is clearly off:
    // the cards re-place themselves after every zoom, so chasing small differences would only make the map wander.
    if (owner !== 'landing') return;
    const size = map.getSize();
    const cardIds = Array.from(map.getContainer().querySelectorAll<HTMLElement>('.zc-card-group.on [data-zone-card]'))
      .map(el => el.dataset.zoneCard ?? '').sort().join(',');
    const signature = `${cardIds}|${size.x}x${size.y}`;
    if (signature !== composeSignature.current) { composeSignature.current = signature; composePasses.current = 0; }
    if (composePasses.current >= 1) return;

    const points = scopedZonesRef.current.filter(zone => !zone.archivedAt).flatMap(polygonLatLngs);
    if (points.length === 0) return;
    if (composeView(map, latLngBounds(points), null, COMPOSE_MARGIN_RATIO, { px: 24, zoom: 0.08 }, true, false)) composePasses.current += 1;
  }, [focusZoneId]);

  // Closing the panel returns to the whole venue, so the operator is never left zoomed in on one zone.
  const closeZonePanel = useCallback(() => {
    setSelectedZoneId(null);
    setLandingPick(null);
    framing.current = 'landing';
    handsOffUntil.current = Date.now() + 900;
    // The map box grows when the panel goes: re-measure it after React has applied the layout, then glide (once) back
    // to the whole venue.
    setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;
      map.invalidateSize({ animate: false });
      const zones = scopedZonesRef.current.filter(zone => !zone.archivedAt);
      if (!revealEnded.current) { fitZoneLayout(map, zones); return; }
      const points = zones.flatMap(polygonLatLngs);
      if (points.length > 0) composeView(map, latLngBounds(points), null, COMPOSE_MARGIN_RATIO, { px: 4, zoom: 0.02 }, true, false);
    }, 0);
  }, []);

  // Escape closes the zone panel, so getting back to the whole venue never needs a trip to the panel's corner.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !selectedZoneId) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      closeZonePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedZoneId, closeZonePanel]);

  useEffect(() => {
    if (!focusZoneId || selectedZoneId !== focusZoneId) return;
    if (!selectedZone || !mapRef.current || selectedZone.geometry.type !== 'Polygon') return;
    mapRef.current.invalidateSize({ animate: false });
    framing.current = { zoneId: selectedZone.id };
    focusZoneInView(mapRef.current, selectedZone);
  }, [focusZoneId, selectedZoneId, selectedZone]);

  // Map density opacity from calibration slider (higher calibration = more visible heatmap)
  const heatmapOpacity = (calibration / 100) * 0.85;
  const firstBasemap = basemapIds[0];
  const activeBasemap = basemaps[basemap] ? basemap : defaultBasemap;
  const tileLayer = basemaps[activeBasemap] ?? (firstBasemap ? basemaps[firstBasemap] : undefined);
  const renderedTileLayer = usingBasemapFallback && tileLayer?.fallback
    ? tileLayer.fallback
    : tileLayer;

  useEffect(() => {
    tileErrorCount.current = 0;
    setUsingBasemapFallback(false);
  }, [activeBasemap, tileLayer?.tileUrl]);

  const handleTileError = useCallback(() => {
    if (!tileLayer?.fallback || usingBasemapFallback) return;
    tileErrorCount.current += 1;
    if (tileErrorCount.current >= 3) setUsingBasemapFallback(true);
  }, [tileLayer?.fallback, usingBasemapFallback]);

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

      {/* ── Map ───────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative', minWidth: 0, minHeight: 0 }}>
        <MapContainer
          center={MAP_CENTER}
          zoom={MAP_ZOOM}
          zoomSnap={0.25}
          style={{ width: '100%', height: '100%', opacity: 0, transition: 'opacity 0.45s ease' }}
          zoomControl={false}
          attributionControl={false}
        >
          <MapController mapRef={mapRef} onResize={refitOnResize} onUserInteract={markUserMovedMap} onReady={markMapReady} />

          {renderedTileLayer && (
            <TileLayer
              key={`${activeBasemap}:${usingBasemapFallback ? 'fallback' : 'primary'}`}
              url={renderedTileLayer.tileUrl}
              attribution={renderedTileLayer.attribution}
              maxZoom={renderedTileLayer.maxZoom}
              maxNativeZoom={usingBasemapFallback ? undefined : tileLayer?.maxNativeZoom}
              eventHandlers={{ tileerror: handleTileError }}
            />
          )}
          <ZoomControl position="topright" />
          {!renderedTileLayer && (
            <div data-map-obstacle="bottom" style={{
              position: 'absolute', left: 14, bottom: providerMessageBottom, zIndex: 500,
              maxWidth: 360, padding: '8px 10px', borderRadius: 5,
              background: `${C.surface}f2`, border: `1px solid ${C.border2}`,
              color: C.muted, fontSize: 11, lineHeight: 1.4,
              fontFamily: "'Albert Sans', sans-serif",
            }}>
              {basemapsLoading ? 'Loading map provider configuration...' : basemapsError ?? 'Map provider configuration is unavailable.'}
            </div>
          )}

          {layers.heatmap && (
            <DensityHeatmap zones={zones} snapshots={displayDensity} opacity={heatmapOpacity} />
          )}
          {layers.boundaries && (
            <ZoneBoundaryLayer
              zones={zones}
              snapshots={displayDensity}
              alertCounts={activeAlertCountByZone}
              hiddenTagIds={hiddenTagIds}
              showTags={layers.tags}
              onHideTag={hideTag}
              onCalloutsSettled={recompose}
              holdReveal={!landingShown}
              selectedZoneId={selectedZoneId}
              onZoneClick={selectZone}
              C={C}
            />
          )}
          {layers.vectors && (
            <MovementVectorLayer
              zones={zones}
              snapshots={playback.position < windowMinutes ? {} : projectedSnapshots}
              C={C}
              onZoneClick={selectZone}
            />
          )}
          {layers.alerts && activeAlerts.map((alert, alertIndex) => {
            const zone = zones.find(z => z.id === alert.zoneId);
            if (!zone || zone.geometry.type !== 'Polygon') return null;
            const centroid = polygonCentroid(zone.geometry.coordinates as number[][][]) as LatLngTuple;
            // Several alerts on one zone would sit on exactly the same spot, hiding all but the top one: fan them out
            // sideways so each can be seen and hovered.
            const sameZoneBefore = activeAlerts.slice(0, alertIndex).filter(other => other.zoneId === alert.zoneId).length;
            const anchor = alertMarkerPosition(centroid);
            const position: LatLngTuple = [anchor[0], anchor[1] + sameZoneBefore * MAP_MARKER_OFFSET_DEGREES * 2.4];
            return (
              <CircleMarker
                key={alert.id}
                center={position}
                radius={8}
                pathOptions={{ color: C.critical, fillColor: C.critical, fillOpacity: 0.9, weight: 2, className: 'crowd-map-alert-marker' }}
                eventHandlers={{ click: () => { recordAlertDrilldown(alert); selectZone(zone); } }}
              >
                <Tooltip direction="top" offset={[0, -10]} className="crowd-hover-tip" opacity={1}>
                  <MapTip
                    C={C}
                    accent={SeverityColor(C, alert.severity)}
                    title={`${alertLabel(alert.alertType)} · ${zone.name}`}
                    detail={[capitalise(alert.severity), capitalise(alert.status), minutesAgoLabel(alert.triggeredAt)].filter(Boolean).join(' · ')}
                  />
                </Tooltip>
              </CircleMarker>
            );
          })}
          {layers.flags && scopedFlags.map(flag => {
            const zoneId = flag.monitoredZoneIds[0];
            if (!zoneId) return null;
            const zone = zones.find(z => z.id === zoneId);
            if (!zone || zone.geometry.type !== 'Polygon') return null;
            const centroid = polygonCentroid(zone.geometry.coordinates as number[][][]) as LatLngTuple;
            const color = flag.priority === 'urgent' ? C.critical : C.amber;
            return (
              <CircleMarker
                key={flag.flagId}
                center={flagMarkerPosition(centroid)}
                radius={6}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: 1.5, className: 'crowd-map-flag-marker' }}
                eventHandlers={{ click: () => selectZone(zone) }}
              >
                <Tooltip direction="top" offset={[0, -8]} className="crowd-hover-tip" opacity={1}>
                  <MapTip C={C} accent={color} title={`Anonymous flag · ${zone.name}`} detail={flag.priority === 'urgent' ? 'Urgent priority' : 'Routine priority'} />
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Layer control panel — top-left overlay */}
        <LayerPanel
          C={C}
          layers={layers}
          dispatch={dispatchLayer}
          calibration={calibration}
          setCalibration={setCalibration}
          basemap={activeBasemap}
          setBasemap={setBasemap}
          basemapIds={basemapIds}
          onBasemapChange={key => recordMapControl('basemap', key)}
          onLayerToggle={(key, enabled) => {
            recordActivity({
              action: CROWD_CLIENT_ACTIVITY_ACTION.mapLayerChanged,
              page: '/map',
              operationId: selectedOperation?.id,
              resourceType: CROWD_CLIENT_RESOURCE_TYPE.mapLayer,
              resourceId: key,
              metadata: { layer: key, enabled },
            });
          }}
          onCalibrationCommit={value => recordMapControl('density_calibration', value)}
          hiddenTagCount={hiddenTagIds.size}
          onRestoreTags={() => setHiddenTagIds(new Set())}
        />

        {/* Playback scrubber — bottom overlay */}
        <PlaybackScrubber
          C={C}
          pb={playback}
          setPb={setPlayback}
          timeWindow={timeWindow}
          windowMinutes={windowMinutes}
          markers={timelineMarkers}
          operationBounds={operationBounds}
          onPlaybackChange={recordPlayback}
        />

        <AlertTicker
          C={C}
          alerts={activeAlerts}
          zoneNameById={operationZoneNameById}
          onAlertClick={(alert) => {
            recordAlertDrilldown(alert);
            const zone = zones.find(z => z.id === alert.zoneId);
            if (zone) selectZone(zone);
          }}
        />

        {/* Map attribution — separate from LayerPanel per prototype */}
        <div data-map-obstacle="bottom" style={{
          position: 'absolute', left: 14, bottom: providerMessageBottom, zIndex: 500, maxWidth: 300,
          background: `${C.surface}cc`, backdropFilter: 'blur(6px)',
          border: `1px solid ${C.border2}`, borderRadius: 4, padding: '5px 10px',
          fontSize: 9.5, color: C.muted, fontFamily: "'Albert Sans', sans-serif",
          lineHeight: 1.35, textAlign: 'left',
        }}>
          <span style={{ fontFamily: 'monospace', color: C.teal, fontWeight: 600 }}>Leaflet</span>
          {' · '}
          <span>{renderedTileLayer?.attribution.replace(/&copy;/g, '©') ?? 'Map provider configuration unavailable'}{usingBasemapFallback ? ' (fallback)' : ''}</span>
        </div>
      </div>

      {/* ── Zone status panel — right (visible when zone selected) ───────── */}
      {selectedZone && (
        // The panel and the map arrive together: neither is shown before the other has been framed.
        <div style={{ display: 'flex', flexShrink: 0, opacity: landingShown ? 1 : 0, transition: 'opacity 0.45s ease' }}>
        <ZoneStatusPanel
          zone={selectedZone}
          snap={displayDensity[selectedZone.id]}
          metric={selectedZoneMetric}
          densitySparkline={selectedZoneDensitySparkline}
          surgeRate5Min={selectedZoneSurgeRate}
          trendUnavailable={isPlaybackTrendUnavailable}
          projectionStatus={projectionStatus}
          operationName={selectedOperation?.name ?? null}
          priorityNote={landingPick?.zoneId === selectedZone.id ? landingPick.note : null}
          activeAlertCount={selectedZoneAlertCount}
          flaggedTokenCount={selectedZoneFlagCount}
          C={C}
          onClose={closeZonePanel}
          zones={zones}
          onZoneChange={(id) => {
            const z = zones.find(z => z.id === id);
            if (z) selectZone(z);
          }}
        />
        </div>
      )}

      <style>{`
        @keyframes alert-pulse {
          0%, 100% { box-shadow: 0 0 0 3px ${C.green}33; }
          50% { box-shadow: 0 0 0 8px transparent; }
        }
        /* The ticker holds still under the pointer, so an alert can be read and clicked without chasing it. */
        .alert-ticker-track:hover { animation-play-state: paused !important; }
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
